import { borrowerDetailQueryOptions } from "@/hooks/queries/borrowerDetailQueryOptions";
import { queryKeys } from "@/queryKeys";
import type { BorrowerDetail } from "@/types/borrower";
import { DOC_TYPE_LABELS } from "@/types/statement";

import { defineContexts } from "./contexts/registry";

/**
 * The web app's context types, for the assistant. The one agent file that knows the domain:
 * the route a context lives on (focus and CONTEXT_ROUTES come from it), the queries that show it,
 * refetched when the assistant changes it (`context.changed`), and its documents for @-mention.
 */
export const AGENT_CONTEXTS = defineContexts({
  application: {
    route: "/applications/:id",
    invalidate: (id) =>
      [
        queryKeys.borrower(id), // its checklist too
        ["borrowerTransactions", id],
        queryKeys.decisionsByBorrower(id),
        queryKeys.assessmentByBorrower(id),
        queryKeys.refinanceOfferByBorrower(id),
        queryKeys.baselayerCheck(id),
        ["docusignForm", id],
        ["docusignEnvelopeStatus", id],
        ["contractForm", id],
        ["contractStatus", id],
        queryKeys.positionRunsList(id),
        queryKeys.analysisRunsList(id),
        // Every run's detail: the AI Analysis tab draws from it, and a correction re-runs its figures.
        ["analysisRuns", "detail"],
        queryKeys.applicationQuickLook(id),
        // Lists and counts that include it.
        ["borrowers"],
        ["decisions"],
        queryKeys.dashboardStats(),
      ].map((queryKey) => ({ queryKey })),
    documents: (id) => ({
      ...borrowerDetailQueryOptions(id),
      select: (borrower: BorrowerDetail) =>
        borrower.documents.map((document) => ({
          id: document.id,
          name: document.filename,
          type: document.doc_type ? DOC_TYPE_LABELS[document.doc_type] : null,
          url: document.file_url ?? null,
          // Statement rows carry the statement's id; other files never have one.
          statement: document.bank_statement_id != null,
        })),
    }),
    label: (id) => ({
      ...borrowerDetailQueryOptions(id),
      select: (borrower: BorrowerDetail) => borrower.company_name,
    }),
  },
});
